import React, { useState, useMemo } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
// @ts-ignore: No TypeScript support
import makeComponentTrashable from 'trashable-react';
import { useTranslation, StatusMessage } from 'Shared/util/custom-hooks';
import { activate, activateWithEId } from 'Shared/util/web-api';
import { EIdAuthenticationPurpose } from 'Shared/util/types';
import useAppContext from 'Contexts/app-context';
import useUserContext from 'Contexts/user-context';
import { useEIdAuthentication } from 'Shared/util/eid-authentication/eid-authentication-hook';
import { getLink } from 'Shared/navigation/login/routes';
import { getParameterByName } from 'Shared/util/query-parser';
import Button from 'Shared/components/button';
import InputText from 'Shared/components/input-text';
import Spinner from 'Shared/components/spinner';
import TextLink from 'Shared/components/text-link';
import styles from './activate-account-form.css';


enum ActivationMethod {
  EId,
  Credentials
};

type ActivateAccountFormProps = {
  registerPromise: (p: Promise<any>) => Promise<any>;
  dispatchStatusMessage: (action: StatusMessage) => void;
};

const ActivateAccountForm = ({
  registerPromise,
  dispatchStatusMessage,
}: ActivateAccountFormProps) => {
  const [activationMethod, setActivationMethod] = useState(ActivationMethod.EId);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [validPassword, setValidPassword] = useState(true);
  const [validConfirmPassword, setValidConfirmPassword] = useState(true);
  const [isActivatingAccount, setIsActivatingAccount] = useState(false);
  const history = useHistory();
  const location = useLocation();
  const { appData: { eIdProvider, privacyPolicyUrl } } = useAppContext();
  const { updateUserSessionToken } = useUserContext();
  const t = useTranslation(x => x.login);
  const tdb = useTranslation(x => x.dbTranslations);
  const te = useTranslation(x => x.eId);
  const activationId = useMemo(() => getParameterByName('id', location.search), [location.search]);

  const handleActivationSucceeded = (token: string) => {
    if (updateUserSessionToken) {
      updateUserSessionToken(token);
      history.push(getLink('select-org'));
    } else {
      history.push('/');
    }
  };

  const handleActivationFailed = () => {
    setIsActivatingAccount(false);
    dispatchStatusMessage({ type: 'FAILED', message: t.yourAccountCouldNotBeActivated });
  };

  const handleEIdAuthenticationSucceeded = (token: string) => {
    if (activationId) {
      setIsActivatingAccount(true);

      registerPromise(activateWithEId(activationId, token))
        .then(handleActivationSucceeded)
        .catch(handleActivationFailed);
    }
  };
  const handleEIdAuthenticationFailed = () => dispatchStatusMessage({ type: 'FAILED', message: tdb.eidVerificationAborted });
  const handleEIdAuthenticationError = () => dispatchStatusMessage({ type: 'FAILED', message: t.yourAccountCouldNotBeActivated });
  const handleEIdAuthenticationStarted = () => dispatchStatusMessage({ type: 'NONE' });
  const {
    renderEIdAuthentication,
    isAuthenticatingEId
  } = useEIdAuthentication(
    registerPromise,
    EIdAuthenticationPurpose.AccountActivation,
    handleEIdAuthenticationSucceeded,
    handleEIdAuthenticationFailed,
    handleEIdAuthenticationError,
    handleEIdAuthenticationStarted
  );

  const handleActivateWithCredentials = (e: React.FormEvent) => {
    e.preventDefault();

    if (activationId) {
      dispatchStatusMessage({ type: 'NONE' });
      setIsActivatingAccount(true);

      registerPromise(activate(activationId, password, confirmPassword))
        .then(handleActivationSucceeded)
        .catch(handleActivationFailed);
    }
  };

  const toggleActivationMethod = () => {
    if (activationMethod === ActivationMethod.Credentials) {
      setPassword('');
      setConfirmPassword('');
      setValidPassword(true);
      setValidConfirmPassword(true);
      setActivationMethod(ActivationMethod.EId);
    }
    else {
      setActivationMethod(ActivationMethod.Credentials);
    }
  };

  return (
    <div>
      {activationMethod === ActivationMethod.EId
        ? isActivatingAccount ? <Spinner /> : renderEIdAuthentication()
        : (
          <form onSubmit={handleActivateWithCredentials}>
            <InputText
              // @ts-ignore: TODO: Fix error
              inputType="new-password"
              value={password}
              onChange={(e: any) => setPassword(e.target.value)}
              setEnabledSubmit={(value: boolean) => setValidPassword(value)}
              isRequired
            />
            <InputText
              // @ts-ignore: TODO: Fix error
              inputType="confirm-password"
              value={confirmPassword}
              onChange={(e: any) => setConfirmPassword(e.target.value)}
              setEnabledSubmit={(value: boolean) => setValidConfirmPassword(value)}
              compareValue={password}
              isRequired
            />
            <Button
              buttonType="action-primary"
              text={t.getStarted}
              attributes={{
                type: 'submit',
                disabled: (activationMethod !== ActivationMethod.Credentials || !validPassword || !validConfirmPassword)
              }}
              isLoading={isActivatingAccount}
              className={styles.submitButton}
            />
          </form>
        )}
      {(!isAuthenticatingEId && !isActivatingAccount) && (
        <TextLink
          text={te.format((activationMethod === ActivationMethod.EId ? te.i_have_no_eid : te.i_want_to_use_eid), te[eIdProvider])}
          link={toggleActivationMethod}
          className={styles.activationMethodTextLink}
        />
      )}
      {(privacyPolicyUrl && !isAuthenticatingEId && !isActivatingAccount) && (
        <TextLink
          text={te.privacyPolicy}
          link={privacyPolicyUrl}
          className={styles.privacyPolicyTextLink}
        />
      )}
    </div>
  );
};

export default makeComponentTrashable(ActivateAccountForm);
